import { createApp, markRaw } from 'vue'
import { createPinia } from 'pinia'
import '@/assets/scss/main.scss'
import App from './App.vue'
import router from './router'
import InlineSvg from 'vue-inline-svg'
import loggerPlugin from './plugins/logger'
import { logger } from './services/loggerService'
import { consoleInterceptor } from './services/consoleInterceptor'
import { errorLogService } from './services/errorLogService'

// Initialize console interceptor immediately to capture all logs
consoleInterceptor.setupInterceptor()

// Initialize error log service after console interceptor
errorLogService.setup()

// Global handler for dynamic import failures (stale deployments)
// This catches chunk loading errors that occur outside of route navigation
window.addEventListener('unhandledrejection', (event) => {
  const error = event.reason
  const isChunkError = error?.message?.includes('Failed to fetch dynamically imported module') ||
                       error?.message?.includes('Loading chunk') ||
                       error?.message?.includes('Loading CSS chunk') ||
                       error?.message?.includes('Unable to preload CSS')

  if (isChunkError) {
    event.preventDefault() // Prevent the error from appearing in console
    logger.warn('[Main] Dynamic import failed, reloading for fresh assets', {
      error: error?.message
    })

    // Store current path for redirect after reload
    sessionStorage.setItem('chunkErrorRedirect', window.location.pathname + window.location.search)

    // Force reload to get latest assets
    window.location.reload()
  }
})

// Vite-specific preload error handler
window.addEventListener('vite:preloadError', (event) => {
  event.preventDefault()
  logger.warn('[Main] Vite preload error, reloading for fresh assets', {
    payload: event.payload
  })

  sessionStorage.setItem('chunkErrorRedirect', window.location.pathname + window.location.search)
  window.location.reload()
})

/* Custom FontAwesome component - works with CDN Kit for auto-subsetting */
import FontAwesomeIcon from '@/components/global/FontAwesomeIcon.vue'

const pinia = createPinia()

// Expose pinia globally for error logging service to access user context
window.__PINIA__ = pinia

pinia.use(({ store }) => {
    store.router = markRaw(router)
})

const app = createApp(App)

app.use(pinia)
app.use(router)
app.use(loggerPlugin)
app.component("inline-svg", InlineSvg)
app.component('font-awesome-icon', FontAwesomeIcon)

// Use our new logger for global error handling with improved error recovery
app.config.errorHandler = (err, instance, info) => {
  // Extract error details safely
  const errorMessage = err?.message || err?.toString() || 'Unknown error';
  const componentName = instance?.$options?.__name || instance?.$options?.name || 'Unknown Component';
  const componentFile = instance?.$options?.__file || 'Unknown file';

  // Log with full context
  logger.error('Global Error:', {
    error: errorMessage,
    stack: err?.stack,
    component: componentName,
    file: componentFile,
    lifecycleHook: info
  });

  // Check for common Vue lifecycle errors
  const isLifecycleError = errorMessage.includes('Cannot read properties of null') ||
                           errorMessage.includes('Cannot read properties of undefined') ||
                           errorMessage.includes('insertBefore') ||
                           errorMessage.includes('removeChild');

  if (isLifecycleError) {
    logger.warn('[Vue] Lifecycle error detected - component may be accessing DOM during unmount', {
      component: componentName,
      hook: info,
      suggestion: 'Add onBeforeUnmount guard or check element existence before DOM manipulation'
    });
  }

  // Check for minified function errors (production build issues)
  const isMinifiedError = /^[a-z]\sis not a function$/i.test(errorMessage) ||
                          /^[a-z]+\sis not defined$/i.test(errorMessage);

  if (isMinifiedError) {
    logger.error('[Vue] Minified function error - possible missing import or circular dependency', {
      component: componentName,
      error: errorMessage,
      suggestion: 'Check component imports and ensure all methods are properly defined'
    });
  }

  // Capture to error log service for Firestore storage
  errorLogService.captureError(err, 'vue', {
    componentName: componentName,
    componentFile: componentFile,
    lifecycleHook: info,
    isLifecycleError: isLifecycleError,
    isMinifiedError: isMinifiedError
  });
};

// Add warning handler for development debugging
app.config.warnHandler = (msg, instance, trace) => {
  if (import.meta.env.DEV) {
    logger.warn('[Vue Warning]', {
      message: msg,
      component: instance?.$options?.__name || instance?.$options?.name || 'Unknown',
      trace: trace
    });
  }
};

app.mount('#app')
